import React, { useEffect, useMemo } from "react";
import styled from "styled-components";
import { UI_BODY_SM, colors2 } from "oolib";
import { useDynamicLayoutHeight } from "@/components/analysisModules/InsightMatrix/utils/useDynamicLayoutHeight";
import { DrilldownDonut } from "@/components/dataviz/DrilldownDonut";
import { swatch } from "@/components/dataviz/DrilldownDonut/chart/palette";
import { DonutLevelPanel } from "../DonutLevelPanel";

// Same border treatment as the topic cluster cards.
const WidgetCard = styled.div`
  border: 1px solid ${colors2.grey10};
  border-radius: 12px;
  background: ${colors2.white};
  height: 100%;
  min-height: 0;
  overflow: hidden;
`;

const Row = styled.div`
  display: flex;
  gap: 16px;
  height: 100%;
  min-height: 0;
`;

const DonutCell = styled(WidgetCard)`
  flex: 1 1 62%;
  min-width: 0;
  padding: 12px;
`;

const PanelCell = styled(WidgetCard)`
  flex: 1 1 38%;
  min-width: 260px;
  max-width: 420px;
`;
import { buildDonutData } from "./buildDonutData";
import { useSignalCategories } from "../SignalCategoriesContext";

/**
 * Layout 3 — the drill-down donut. Everything Topics-specific lives HERE (and
 * in the adapter): DrilldownDonut (components/dataviz) is a generic any-tree
 * component.
 * Pivots the same tree the table and card layouts render into a signal-first
 * hierarchy (category → signal → topic bucket → finding) and hands it to the
 * chart. Clicking a finding leaf opens the shared annotation side panel
 * through the same onRowClick contract as the other layouts, passing the
 * finding's original tree row — identical panel, identical content.
 */
export const DonutLayout = ({
  tree,
  signalMetaById,
  onRowClick,
  period,
  onTimeBinClick,
  // Drill path state lives in the parent (TopicClusteringTreeView) so the
  // page-level breadcrumb above the widgets can render and jump it.
  activePathIds,
  onActivePathChange,
  onTrailChange,
}) => {
  const categoryTheme = useSignalCategories();
  const data = useMemo(() => buildDonutData(tree, signalMetaById, categoryTheme), [tree, signalMetaById, categoryTheme]);

  // Resolve the chart's drill path (ids, root-first) to the CURRENT level for
  // the side panel: the focused node's children — or the top ring when zoomed
  // out. Colours must MATCH the chart's segments, so uncoloured nodes
  // (buckets/findings) take the same sibling-position swatch the chart's
  // fallback palette assigns — same array order, same swatch() call.
  const drillInto = (n) =>
    n.children?.length
      ? onActivePathChange([...activePathIds, n.id])
      : n.row && onRowClick(n.row);
  const level = useMemo(() => {
    let nodes = data.children;
    const trail = [];
    for (const id of activePathIds) {
      const next = nodes.find((n) => n.id === id);
      if (!next) break;
      trail.push(next.label);
      nodes = next.children || [];
    }
    return {
      trail,
      nodes: nodes.map((n, i) => ({ ...n, color: n.color || swatch(i) })),
    };
  }, [activePathIds, data]);

  // Report the resolved trail (labels, root-first) up for the breadcrumb.
  useEffect(() => {
    onTrailChange?.(level.trail);
  }, [level.trail]); // eslint-disable-line react-hooks/exhaustive-deps

  // Stretch the chart to the viewport bottom from wherever it actually sits —
  // the chrome above (meta line, tabs) is query-driven, not a fixed height.
  const { parentWrapperRef, gridHeightCSS } = useDynamicLayoutHeight({
    // The wrapper sits inside ContentPad (20px bottom padding) inside the
    // sidebar layout's content section, whose own height already ends 1px
    // short of the viewport — so the row must stop 20px + a hair earlier or
    // the section scrolls by exactly that overshoot.
    offset: 24,
    observeLayoutChanges: true,
  });

  if (!data.children.length) {
    return (
      <UI_BODY_SM style={{ color: colors2.grey60 }}>
        Nothing placed in the tree yet — the donut appears with the first clustered observations.
      </UI_BODY_SM>
    );
  }

  return (
    <div ref={parentWrapperRef} style={{ height: gridHeightCSS }}>
      <Row>
        <DonutCell>
          <DrilldownDonut
            data={data}
            ariaLabel="Signals and topics distribution"
            onLeafClick={(node) => node.row && onRowClick(node.row)}
            activePath={activePathIds}
            onActivePathChange={onActivePathChange}
          />
        </DonutCell>
        <PanelCell>
          <DonutLevelPanel
            nodes={level.nodes}
            period={period}
            // Mirror of a segment click on the chart: nodes with children
            // drill in, leaves open the shared annotation panel.
            onDrill={drillInto}
            // A time-segment click on the shared chart narrows the period to
            // that bin for the whole level (week bins / dataless months toast
            // instead — see handleTimeBinClick).
            onBinClick={(key) => onTimeBinClick?.(key)}
          />
        </PanelCell>
      </Row>
    </div>
  );
};
